import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getPharmacyStores, updateUserMetadata } from '../redux/modules/user/actions';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

/**
 * StoreSelectionList component displays pharmacy stores in an expandable table format
 * This component is shown when user has SuperUser role
 */
const StoreSelectionList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pharmacyStores, pharmacyStoresLoading, pharmacyStoresError, currentLoggedInUserDetail } = useSelector(state => state.users);
  const [expandedPharmacies, setExpandedPharmacies] = useState(new Set());

  // Fetch pharmacy stores data when component mounts
  useEffect(() => {
    dispatch(getPharmacyStores());
  }, [dispatch]);

  /**
   * Toggles the expanded state of a pharmacy
   * @param {string} pharmacyId - The ID of the pharmacy to toggle
   */
  const togglePharmacyExpansion = (pharmacyId) => {
    const newExpanded = new Set(expandedPharmacies);
    if (newExpanded.has(pharmacyId)) {
      newExpanded.delete(pharmacyId);
    } else {
      newExpanded.add(pharmacyId);
    }
    setExpandedPharmacies(newExpanded);
  };

  /**
   * Handles store selection and updates user metadata
   * @param {string} storeId - The ID of the store selected by the user
   */
  const handleStoreClick = (storeId) => {
    // Get userId from currentLoggedInUserDetail
    const userId = currentLoggedInUserDetail?.userId;
    
    if (!userId) {
      console.error('User ID not found in currentLoggedInUserDetail');
      return;
    }
    
    // Dispatch action to update user metadata with selected store
    dispatch(updateUserMetadata(userId, storeId, navigate));
  };

  /**
   * Renders the pharmacy row with expand/collapse functionality
   * @param {Object} pharmacy - Pharmacy data object
   * @returns {JSX.Element} Pharmacy row component
   */
  const renderPharmacyRow = (pharmacy) => {
    const isExpanded = expandedPharmacies.has(pharmacy.pharmacyId);
    const hasStores = pharmacy.stores && pharmacy.stores.length > 0;

    return (
      <React.Fragment key={pharmacy.pharmacyId}>
        {/* Pharmacy Row */}
        <tr className="transition-colors duration-300 hover:bg-blue-100">
          <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 whitespace-nowrap sm:pl-6">
            <div className="flex items-center space-x-3">
              {hasStores && (
                <button
                  onClick={() => togglePharmacyExpansion(pharmacy.pharmacyId)}
                  className="p-1 hover:bg-gray-200 rounded transition-colors"
                >
                  {isExpanded ? (
                    <ChevronDownIcon className="h-4 w-4 text-gray-600" />
                  ) : (
                    <ChevronRightIcon className="h-4 w-4 text-gray-600" />
                  )}
                </button>
              )}
              <span className="font-semibold text-teal-600 text-body-sm">
                {pharmacy.pharmacyName}
              </span>
            </div>
          </td>
          <td className="px-3 py-4 text-sm text-gray-700 whitespace-nowrap">
            {pharmacy.pharmacyAddress}
          </td>
          <td className="px-3 py-4 text-sm text-gray-700 whitespace-nowrap">
            {pharmacy.pharmacyId}
          </td>
          <td className="px-3 py-4 text-sm text-gray-700 whitespace-nowrap">
            -
          </td>
          <td className="px-3 py-4 text-sm text-gray-700 whitespace-nowrap">
            {pharmacy.stores?.length || 0} stores
          </td>
        </tr>

        {/* Store Rows (when expanded) */}
        {isExpanded && hasStores && pharmacy.stores.map((store) => (
          <tr key={store.storeId} className="bg-gray-50 hover:bg-gray-100 transition-colors duration-300">
            <td className="py-3 pl-16 pr-3 text-sm text-gray-600 whitespace-nowrap sm:pl-20 cursor-pointer" onClick={() => handleStoreClick(store.storeId)}>
              <span className="text-gray-700">{store.storeName}</span>
            </td>
            <td className="px-3 py-3 text-sm text-gray-600 whitespace-nowrap">
              {store.storeAddress}
            </td>
            <td className="px-3 py-3 text-sm text-gray-600 whitespace-nowrap">
              {store.storePostalCode}
            </td>
            <td className="px-3 py-3 text-sm text-gray-600 whitespace-nowrap">
              {store.storePrimaryContactPerson || 'N/A'}
            </td>
            <td className="px-3 py-3 text-sm text-gray-600 whitespace-nowrap">
              -
            </td>
          </tr>
        ))}
      </React.Fragment>
    );
  };

  // Show loading state
  if (pharmacyStoresLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
        <span className="ml-2 text-gray-600">Loading pharmacy stores...</span>
      </div>
    );
  }

  // Show error state
  if (pharmacyStoresError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="text-red-600">
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error loading pharmacy stores</h3>
            <p className="text-sm text-red-700 mt-1">{pharmacyStoresError}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      {/* Header section matching patient list style */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Pharmacy Stores</h1>
        <p className="text-sm text-gray-600 mt-1">Select a pharmacy to view its stores</p>
      </div>
      
      {/* Pharmacy stores table */}
      <div className="flow-root bg-white">
        <div className="overflow-x-auto">
          <div className="inline-block min-w-full align-middle">
            <div className="overflow-hidden border border-gray-50 rounded-[4px]">
              <table className="min-w-full divide-y divide-gray-100">
                <thead className="bg-gray-20">
                  <tr>
                    <th
                      scope="col"
                      className="py-3.5 pl-4 w-[20%] pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                    >
                      Pharmacy/Store Name
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Address
                    </th>
                    <th
                      scope="col"
                      className="px-3 w-[10%] py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      ID/Postal Code
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 w-[15%] text-left text-sm font-semibold text-gray-900"
                    >
                      Primary Contact
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 w-[10%] text-left text-sm font-semibold text-gray-900"
                    >
                      Store Count
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {!pharmacyStores || pharmacyStores.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="py-4 font-medium text-center text-gray-900">
                        No pharmacy stores found
                      </td>
                    </tr>
                  ) : (
                    pharmacyStores.map(renderPharmacyRow)
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoreSelectionList;
